import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatError = (detail) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
    if (detail?.msg) return detail.msg;
    return "Error al iniciar sesión";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Introduce email y contraseña");
      return;
    }
    setIsSubmitting(true);
    try {
      await login(email, password);
      toast.success("Sesión iniciada");
      navigate("/admin/leads");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatError(detail));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-galvani-bg relative overflow-hidden flex items-center justify-center px-4" data-testid="admin-login-page">
      {/* Decorative orbs */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-blue-300/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-300/30 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        {/* Back link */}
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm text-galvani-textLight hover:text-galvani-purple font-body transition-colors mb-6"
          data-testid="admin-login-back"
        >
          <ArrowLeft size={16} />
          Volver a la web
        </a>

        <div className="p-8 md:p-10 rounded-3xl bg-white border border-slate-200 shadow-2xl">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-galvani-blue to-galvani-purple flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-heading font-extrabold text-2xl text-galvani-text mb-2">
              Panel de <span className="gradient-text">Administración</span>
            </h1>
            <p className="font-body text-sm text-galvani-textLight">
              Introduce tus credenciales para acceder.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">Email</Label>
              <Input
                type="email"
                placeholder="admin@galvanistudios.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl"
                autoComplete="email"
                data-testid="admin-login-email"
              />
            </div>

            <div>
              <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">Contraseña</Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl pr-11"
                  autoComplete="current-password"
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-galvani-textLight hover:text-galvani-text transition-colors"
                  tabIndex={-1}
                  data-testid="admin-login-toggle-pwd"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold h-12 rounded-full transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-blue-500/25"
              data-testid="admin-login-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Accediendo...
                </>
              ) : (
                "Iniciar sesión"
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
